/**
 * Auth V3 — device cryptographic identity.
 *
 * Generates and persists an ECDSA P-256 key pair for this device. The private key
 * is generated NON-EXTRACTABLE and stored as an opaque CryptoKey in IndexedDB, so
 * it can sign but can never be read out. The public key is exported once as base64
 * SPKI/DER for the pairing request.
 *
 * Why ECDSA P-256: Web Crypto's ECDSA-P256 is universally available across
 * iOS/Android WebViews, and its `sign` output is raw r‖s (IEEE-P1363) — exactly
 * what the V3 backend verifies (SHA-256). Ed25519 in crypto.subtle is not reliably
 * available on those WebViews.
 *
 * Signature encoding: STANDARD base64 of the raw signature (NOT base64url) — see
 * docs/authentication/business-device-pairing-v3-client.md (`btoa(String.fromCharCode(...))`).
 * Public key encoding: standard base64 of the SPKI/DER bytes.
 */

import { Device } from '@capacitor/device';
import type { DeviceSignatureAlgorithm } from '@am3nd-org/pointcraft-types';

import { recordAuthV3Diagnostic } from './diagnostics';

// ----------------------------------------------------------------------

/** The `publicKeyAlgorithm` value sent on the V3 pairing request. */
export const DEVICE_PUBLIC_KEY_ALGORITHM: DeviceSignatureAlgorithm = 'P-256';

const ECDSA_PARAMS: EcKeyGenParams = { name: 'ECDSA', namedCurve: 'P-256' };
const SIGN_PARAMS: EcdsaParams = { name: 'ECDSA', hash: 'SHA-256' };

const IDB_NAME = 'pointcraft-auth-v3';
const IDB_STORE = 'device-identity';
const KEYPAIR_KEY = 'keyPair';
const META_KEY = 'meta';

interface DeviceIdentityMeta {
  fingerprint: string;
  publicKeyBase64: string;
  publicKeyFingerprint: string;
  /** Account that owns this keypair. Missing only on identities created before account scoping. */
  accountNumber?: string;
}

export interface DeviceIdentity extends DeviceIdentityMeta {
  privateKey: CryptoKey;
}

// ----------------------------------------------------------------------
// Minimal promisified IndexedDB helpers (no external deps).

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this environment'));
      return;
    }
    const request = indexedDB.open(IDB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };
    request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB'));
  });
}

function idbGet<T>(db: IDBDatabase, key: string): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const request = tx.objectStore(IDB_STORE).get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error ?? new Error(`Failed to read ${key}`));
  });
}

function idbPut(db: IDBDatabase, key: string, value: unknown): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error(`Failed to write ${key}`));
  });
}

function idbDeleteIdentity(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    store.delete(KEYPAIR_KEY);
    store.delete(META_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Failed to delete the device identity'));
  });
}

// ----------------------------------------------------------------------
// Encoding helpers.

function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function bufferToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

// ----------------------------------------------------------------------

function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('Web Crypto (crypto.subtle) is not available; cannot use Auth V3 device login');
  }
  return subtle;
}

async function resolveFingerprint(): Promise<string> {
  try {
    const { identifier } = await Device.getId();
    if (identifier) return identifier;
  } catch {
    // Device plugin unavailable (e.g. plain web) — fall back to a random UUID below.
  }
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // Last-resort fallback for very old environments.
  return `dev-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export async function computePublicKeyFingerprint(publicKeyBase64: string): Promise<string> {
  const publicKeyBytes = new TextEncoder().encode(publicKeyBase64.trim());
  const digest = await getSubtle().digest('SHA-256', publicKeyBytes);
  return bufferToHex(digest);
}

function normalizeAccountNumber(accountNumber: string | null | undefined): string | undefined {
  const normalized = accountNumber?.replace(/\D/g, '');
  return normalized || undefined;
}

export function isDeviceIdentityAccountMismatch(
  existingAccountNumber: string | null | undefined,
  requestedAccountNumber: string | null | undefined
): boolean {
  const requested = normalizeAccountNumber(requestedAccountNumber);
  if (!requested) return false;
  return normalizeAccountNumber(existingAccountNumber) !== requested;
}

export async function requestPersistentDeviceIdentityStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
  try {
    const persisted = await navigator.storage.persist();
    recordAuthV3Diagnostic('device_identity_storage_persistence_requested', { persisted });
    return persisted;
  } catch (error) {
    recordAuthV3Diagnostic('device_identity_storage_persistence_failed', {
      errorName: error instanceof Error ? error.name : 'unknown',
    });
    return false;
  }
}

async function readDeviceIdentity(db: IDBDatabase): Promise<DeviceIdentity | null> {
  const existingPair = await idbGet<CryptoKeyPair>(db, KEYPAIR_KEY);
  const existingMeta = await idbGet<DeviceIdentityMeta>(db, META_KEY);
  if (!existingPair?.privateKey || !existingMeta?.publicKeyBase64 || !existingMeta.fingerprint) {
    return null;
  }

  const publicKeyFingerprint = await computePublicKeyFingerprint(existingMeta.publicKeyBase64);
  if (existingMeta.publicKeyFingerprint !== publicKeyFingerprint) {
    await idbPut(db, META_KEY, { ...existingMeta, publicKeyFingerprint });
  }

  return { privateKey: existingPair.privateKey, ...existingMeta, publicKeyFingerprint };
}

/** Load the signing identity without generating or replacing key material. */
export async function loadDeviceIdentity(): Promise<DeviceIdentity | null> {
  const db = await openIdb();
  try {
    return await readDeviceIdentity(db);
  } finally {
    db.close();
  }
}

/**
 * Load the persisted device identity, generating and storing a fresh key pair on
 * first use. The returned private key is a non-extractable CryptoKey usable only for
 * signing; the public key and fingerprint are returned for the pairing request.
 *
 * Once created, keypairs are never rotated implicitly by account login. The
 * non-secret session cache may be missing while this key still identifies a paired
 * device, so only explicit logout/unpair/reset paths may destroy it.
 */
export async function getOrCreateDeviceIdentity(
  accountNumber?: string | null
): Promise<DeviceIdentity> {
  const subtle = getSubtle();
  const db = await openIdb();
  try {
    const requestedAccountNumber = normalizeAccountNumber(accountNumber);

    const existingIdentity = await readDeviceIdentity(db);
    if (existingIdentity) {
      console.log('[DeviceIdentity] [getOrCreateDeviceIdentity]: Found existing device identity');
      if (isDeviceIdentityAccountMismatch(existingIdentity.accountNumber, requestedAccountNumber)) {
        recordAuthV3Diagnostic('device_identity_retained_during_account_login', {
          accountScopeMismatch: true,
        });
      }
      return existingIdentity;
    }

    console.log(
      '[DeviceIdentity] [getOrCreateDeviceIdentity]: No existing device identity found, generating new one'
    );
    await requestPersistentDeviceIdentityStorage();

    // Generate a new non-extractable ECDSA P-256 key pair.
    // (For an EC key pair, the public key is always extractable; passing `false`
    // only makes the PRIVATE key non-extractable.)
    const keyPair = (await subtle.generateKey(ECDSA_PARAMS, false, [
      'sign',
      'verify',
    ])) as CryptoKeyPair;
    const spki = await subtle.exportKey('spki', keyPair.publicKey);
    const publicKeyBase64 = bufferToBase64(spki);
    const publicKeyFingerprint = await computePublicKeyFingerprint(publicKeyBase64);
    const fingerprint = await resolveFingerprint();

    const meta: DeviceIdentityMeta = {
      fingerprint,
      publicKeyBase64,
      publicKeyFingerprint,
      ...(requestedAccountNumber ? { accountNumber: requestedAccountNumber } : {}),
    };

    await idbPut(db, KEYPAIR_KEY, keyPair);
    await idbPut(db, META_KEY, meta);
    recordAuthV3Diagnostic('device_identity_created', {
      accountScoped: Boolean(requestedAccountNumber),
    });

    return { privateKey: keyPair.privateKey, ...meta };
  } finally {
    db.close();
  }
}

/**
 * Sign the challenge string with the device private key. Signs the exact UTF-8 bytes
 * of the challenge (no wrapper) and returns a STANDARD base64-encoded raw signature
 * (r‖s for ECDSA P-256), matching the V3 contract.
 */
export async function signChallenge(privateKey: CryptoKey, challenge: string): Promise<string> {
  const subtle = getSubtle();
  const data = new TextEncoder().encode(challenge);
  const signature = await subtle.sign(SIGN_PARAMS, privateKey, data);
  return bufferToBase64(signature);
}

/**
 * Permanently delete the stored device identity (key pair + meta). Forces a fresh
 * key pair on next pairing. Not used by the normal flow (re-pairing reuses the key).
 */
export async function resetDeviceIdentity(): Promise<void> {
  try {
    const db = await openIdb();
    try {
      await idbDeleteIdentity(db);
      recordAuthV3Diagnostic('device_identity_destroyed', {}, 'warning');
    } finally {
      db.close();
    }
  } catch {
    // Best-effort; nothing actionable if the store can't be opened.
  }
}
